import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangleIcon } from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
import { useBrand } from '../../hooks/useBrand';
import { tagOutline } from '../../utils/brandStyles';
import { flattenWarnings } from '../../utils/filterVocabulary';
interface TagsLineProps {
  book: any;
}
type Hue = 'indigo' | 'green' | 'pink' | 'blue' | 'emerald' | 'gray';

// Literal palettes so Tailwind generates them. [light, dark]
const PALETTE: Record<Hue, [string, string]> = {
  indigo: ['bg-indigo-50 text-indigo-700 hover:bg-indigo-100', 'bg-indigo-900/30 text-indigo-300 hover:bg-indigo-900/50'],
  green: ['bg-green-50 text-green-700', 'bg-green-900/30 text-green-300'],
  pink: ['bg-pink-50 text-pink-700 hover:bg-pink-100', 'bg-pink-900/30 text-pink-300 hover:bg-pink-900/50'],
  blue: ['bg-blue-50 text-blue-800 hover:bg-blue-100', 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50'],
  emerald: ['bg-emerald-50 text-emerald-700 hover:bg-emerald-100', 'bg-emerald-900/30 text-emerald-300 hover:bg-emerald-900/50'],
  gray: ['bg-gray-100 text-gray-800 hover:bg-gray-200', 'bg-gray-800 text-gray-300 hover:bg-gray-700']
};
const RELATIONSHIP_TYPES = ['why choose', 'polyamory', 'reverse harem', 'ménage', 'triad', 'quad', 'throuple'];
const titleCase = (value: string) => value.split(' ').map(word => word[0].toUpperCase() + word.slice(1)).join(' ');
export const TagsLine = ({
  book
}: TagsLineProps) => {
  const {
    darkMode
  } = useDarkMode();
  const {
    theme,
    content
  } = useBrand();

  /** Shared tag pill: shape + outline from the brand, colour from the hue. */
  const tagClass = (hue: Hue) => `inline-block px-3 py-1 ${theme.tagRadius} text-sm font-medium shadow-sm transition-colors duration-200 ${tagOutline(theme, hue)} ${PALETTE[hue][darkMode ? 1 : 0]}`;
  const warnings = flattenWarnings(book.contentWarnings);
  const isRomance = book.genres?.some((g: string) => g.toLowerCase().includes('romance')) ?? false;

  // Relationship types + pairing structures, de-duplicated
  const relationshipTags: React.ReactNode[] = [];
  if (isRomance && book.characterRelationships) {
    const shownTypes = new Set<string>();
    const shownStructures = new Set<string>();
    book.characterRelationships.forEach((rel: any) => {
      rel.style.forEach((style: string) => {
        const matchedType = RELATIONSHIP_TYPES.find(type => style.toLowerCase().includes(type));
        if (matchedType && !shownTypes.has(matchedType)) {
          shownTypes.add(matchedType);
          relationshipTags.push(<Link key={`type-${matchedType}`} to={`/relationship-types#${matchedType.replace(/\s+/g, '-')}`} className={tagClass('pink')} data-id="element-7462" data-mp="18hms4r3hb60t">
              {titleCase(matchedType)}
            </Link>);
        }
        const structure = style.match(/([MF]{2,})/i)?.[0];
        if (structure && !shownStructures.has(structure)) {
          shownStructures.add(structure);
          relationshipTags.push(<Link key={`structure-${structure}`} to="/pairing-types?category=why-choose" className={tagClass('blue')} data-id="element-7463" data-mp="49yvs3j5shew">
              {structure.toUpperCase()}
            </Link>);
        }
      });
    });
    const shownDynamics = new Set<string>();
    book.characterRelationships.forEach((rel: any) => {
      if (rel.type && !shownDynamics.has(rel.type)) {
        shownDynamics.add(rel.type);
        relationshipTags.push(<Link key={`dynamic-${rel.type}`} to={`/tagged-books?tag=${encodeURIComponent(rel.type)}&category=relationships`} className={tagClass('pink')} data-id="element-7464" data-mp="3cp93ps9qcgul">
            {rel.type}
          </Link>);
      }
    });
  }
  return <div className={`flex flex-wrap gap-2 py-2 border-t border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-7465" data-mp="15xhthsxjpd47">
      {book.genres?.length > 0 && <Link to={`/browse?genre=${encodeURIComponent(book.genres[0])}`} className={tagClass('indigo')} data-id="element-7466" data-mp="7k2p7em6vgdt">
          {book.genres[0]}
        </Link>}
      {book.subgenre && <Link to={`/tagged-books?tag=${encodeURIComponent(book.subgenre)}&category=subgenres`} className={tagClass('indigo')} data-id="element-7467" data-mp="3uoij3pml79jg">
          {book.subgenre}
        </Link>}

      {isRomance && book.characterRelationships && <>
          <span className={tagClass('green')} data-id="element-7468" data-mp="370sjwhpyxyco">{book.endingType || 'HEA'}</span>
          {relationshipTags}
        </>}

      {book.themes?.map((themeName: string) => <Link key={`theme-${themeName}`} to={`/tagged-books?tag=${encodeURIComponent(themeName)}&category=themes`} className={tagClass('indigo')} data-id="element-7469" data-mp="2x230ulmc67uu">
          {themeName}
        </Link>)}
      {book.vibe && <span className={tagClass('indigo')} data-id="element-7470" data-mp="2ig5p58ffwo46">{book.vibe}</span>}
      {book.tropes?.map((trope: string) => <Link key={`trope-${trope}`} to={`/tagged-books?tag=${encodeURIComponent(trope)}&category=tropes`} className={tagClass('blue')} data-id="element-7471" data-mp="2dhufaxp5adly">
          {trope}
        </Link>)}
      {book.setting && <Link to={`/tagged-books?tag=${encodeURIComponent(book.setting)}&category=settings`} className={tagClass('emerald')} data-id="element-7472" data-mp="31e8ifzf0t77s">
          {book.setting}
        </Link>}

      {/* Content warnings: preview tag or a single icon, per brand config */}
      {warnings.length > 0 && (content.bookCard.warningDisplay === 'tags' ? <>
            <span className={`inline-block px-3 py-1 ${theme.tagRadius} text-sm font-medium shadow-sm border border-amber-500/30 bg-amber-900/30 text-amber-300`} data-id="element-7473" data-mp="367b4lmhgxedy">
              {warnings[0]}
            </span>
            {warnings.length > 1 && <span className={`inline-block px-3 py-1 ${theme.tagRadius} text-xs font-medium border border-amber-500/20 bg-amber-900/15 text-amber-400/80`} data-id="element-7474" data-mp="3n85um1rc0wlw">
                +{warnings.length - 1} more
              </span>}
          </> : <Link to="/content-warnings" aria-label={`Content warnings: ${warnings.join(', ')}`} title={`Content Warnings: ${warnings.join(', ')}`} className={`inline-flex items-center justify-center w-8 h-8 rounded-full shadow-sm transition-[transform,background-color] duration-200 hover:scale-110 ${darkMode ? 'bg-amber-900/30 border border-amber-700 text-amber-400 hover:bg-amber-900/50' : 'bg-amber-50 border border-amber-200 text-amber-600 hover:bg-amber-100'}`} data-id="element-7475" data-mp="5re5ssfj4rgg">
            <AlertTriangleIcon className="h-4 w-4" data-id="element-7476" data-mp="2ljbw8lesr99p" />
          </Link>)}

      {book.tags?.filter((tag: string) => !book.themes?.includes(tag) && !book.tropes?.includes(tag) && tag !== book.setting && tag !== book.vibe).map((tag: string) => <Link key={tag} to={`/tagged-books?tag=${encodeURIComponent(tag)}`} className={tagClass('gray')} data-id="element-7477" data-mp="yruj3c4ra4b8">
            {tag}
          </Link>)}
    </div>;
};
export default TagsLine;